import React, { useCallback, useEffect, useState } from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import { Button } from 'components';
import { useHistory } from 'react-router';

import './Tabs.scss';
import useTabsScroll from './useTabsScroll';

const DEFAULT_ACTIVE_TAB = 0;
const TAB_INDEX_PARAM = 'tabIndex';

interface ContentItem<P = {}> {
  tabName: string;
  tabContent: JSX.Element;
  tabLinkProps?: P;
  tabIndex?: number;
  tabIcon?: JSX.Element;
}

type TabsBorder = 'default' | 'top' | 'bottom';

export type TabsProps = {
  border?: TabsBorder;
  className?: string;
  justify?: boolean;
  defaultActiveTab?: number;
  showButtons?: boolean;
  stickyNav?: boolean;
  enableNavScroll?: boolean;
  size?: 'sm' | 'lg' | null;
  topRightContent?: JSX.Element;
  content: ContentItem[];
  linkable?: boolean;
  onChange?: (tab: number, tabName?: string) => void;
  manualActiveTabIndex?: number;
  tabsAppend?: JSX.Element;
  append?: JSX.Element;
  beforeTabsContent?: JSX.Element;
};

const Tabs = ({
  defaultActiveTab = 0,
  showButtons = false,
  justify = true,
  onChange = () => {},
  topRightContent = undefined,
  border = 'default',
  size = null,
  stickyNav = false,
  enableNavScroll = false,
  className = undefined,
  content = [],
  linkable = false,
  manualActiveTabIndex,
  tabsAppend,
  append,
  beforeTabsContent,
}: TabsProps) => {
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const {
    handleScrollButtonClick,
    navScrollRef,
    scrollState,
    updateScrollState,
  } = useTabsScroll({
    content,
    topRightContent,
    tabsAppend,
    append,
  });

  const history = useHistory();

  const setURLParam = useCallback(
    (tabIndex: number) => {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set(TAB_INDEX_PARAM, tabIndex.toString());

      history.replace({
        ...history.location,
        search: `?${searchParams.toString()}`,
      });
    },
    [history]
  );

  const toggleTab = useCallback(
    (tab: number, tabName?: string) => {
      if (activeTab !== tab) {
        setActiveTab(tab);
        (document.activeElement as HTMLButtonElement).blur();

        if (onChange) {
          onChange(tab, tabName);
          if (linkable) setURLParam(tab);
        }
      }
    },
    [activeTab, setActiveTab, onChange, linkable, setURLParam]
  );

  const canScrollLeft = enableNavScroll && scrollState.canScrollLeft;
  const canScrollRight = enableNavScroll && scrollState.canScrollRight;

  const borderClass: Record<TabsBorder, string> = {
    bottom: 'bordered-bottom',
    top: 'bordered-top',
    default: '',
  };

  useEffect(() => {
    const { search } = history.location;
    if (!linkable) return;
    if (new URLSearchParams(search).has(TAB_INDEX_PARAM)) return;

    setURLParam(DEFAULT_ACTIVE_TAB);
  }, [history, linkable, setURLParam]);

  useEffect(() => {
    if (
      manualActiveTabIndex === undefined ||
      manualActiveTabIndex === activeTab
    ) {
      return;
    }
    setActiveTab(manualActiveTabIndex);
    if (linkable) {
      setURLParam(manualActiveTabIndex);
    }
  }, [activeTab, linkable, manualActiveTabIndex, setURLParam]);

  return (
    <div
      className={classnames('tabs', borderClass[border], className, {
        'tabs-sm': size === 'sm',
        'tabs-lg': size === 'lg',
        'nav-justified': justify,
        'tabs--scrollable': enableNavScroll,
      })}
    >
      <div className="tabs-wrap">
        <div className="tabs-nav-wrap">
          <div className="tabs-nav-with-border">
            <div
              className={classnames('tabs-nav-scroll', {
                'has-left-scroll-control': canScrollLeft,
                'has-right-scroll-control': canScrollRight,
              })}
              ref={navScrollRef}
            >
              {canScrollLeft && (
                <button
                  aria-label="Scroll tabs left"
                  className="tabs-scroll-button tabs-scroll-button--left"
                  onClick={() => handleScrollButtonClick('left')}
                  title="Scroll tabs left"
                  type="button"
                />
              )}
              <Nav
                tabs
                className={classnames({
                  'sticky-nav': stickyNav,
                })}
                onScroll={enableNavScroll ? updateScrollState : undefined}
              >
                {content.map((item, index) => {
                  const idx = item.tabIndex ?? index;
                  return (
                    <NavItem key={`${idx}.link`}>
                      {item.tabName && (
                        <NavLink
                          className={classnames({
                            active: activeTab === idx,
                          })}
                          onClick={event => {
                            toggleTab(idx, item.tabName);
                            (event.target as HTMLElement).focus();
                          }}
                          tag="button"
                          type="button"
                          {...item.tabLinkProps}
                        >
                          <div className="d-flex align-items-center">
                            {item.tabIcon && (
                              <div className="mr-1">{item.tabIcon}</div>
                            )}
                            {item.tabName}
                          </div>
                        </NavLink>
                      )}
                    </NavItem>
                  );
                })}
              </Nav>
              {canScrollRight && (
                <button
                  aria-label="Scroll tabs right"
                  className="tabs-scroll-button tabs-scroll-button--right"
                  onClick={() => handleScrollButtonClick('right')}
                  title="Scroll tabs right"
                  type="button"
                />
              )}
            </div>
            {topRightContent && (
              <div className="top-right-content">{topRightContent}</div>
            )}
            {tabsAppend && <div className="tabs-append">{tabsAppend}</div>}
          </div>
          {append}
        </div>
        {beforeTabsContent && (
          <div className="before-tabs-content">{beforeTabsContent}</div>
        )}
        <TabContent activeTab={activeTab}>
          {content.map((item, index) => {
            const idx = item.tabIndex ?? index;
            return (
              <TabPane key={`${idx}.content`} tabId={idx}>
                {item.tabContent}
              </TabPane>
            );
          })}
        </TabContent>
      </div>
      {showButtons && (
        <div className="tab-buttons">
          <div>
            <Button
              color="secondary"
              disabled={activeTab === 0}
              onClick={() => toggleTab(activeTab - 1)}
            >
              Previous
            </Button>
          </div>
          <div>
            <Button
              color="primary"
              disabled={activeTab === content.length - 1}
              onClick={() => toggleTab(activeTab + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tabs;
